import { ref, watch, nextTick } from 'vue'

import { onKeyStroke } from '@vueuse/core'

import type { InputInstance } from 'element-plus'
import type { Ref, ComputedRef } from 'vue'

/** 焦點在輸入框的常數值 */
export const FOCUS_ON_INPUT = -1

export interface UseKeyboardNavigationOptions {
  /**
   * 是否啟用鍵盤導航
   */
  enabled: Ref<boolean> | ComputedRef<boolean>

  /**
   * 可導航的項目總數
   */
  itemCount: Ref<number> | ComputedRef<number>

  /**
   * 容器元素的 ref（用於查找焦點項目並滾動）
   */
  containerRef: Ref<HTMLElement | null>

  /**
   * 焦點項目的 CSS 選擇器
   * @default '.focused'
   */
  focusedSelector?: string

  /**
   * 輸入框的 ref（按 ArrowUp 返回時聚焦）
   */
  inputRef?: Ref<InputInstance | null>

  /**
   * 選擇項目時的回調
   * @param index 被選擇的項目索引
   */
  onSelect?: (index: number) => void

  /**
   * 用於重置焦點的觸發源（例如搜尋關鍵字變化）
   */
  resetTrigger?: Ref<any> | ComputedRef<any>
}

/**
 * 鍵盤導航
 *
 * 提供列表的鍵盤導航功能，支援：
 * - ArrowDown: 向下移動焦點
 * - ArrowUp: 向上移動焦點（到頂部時返回輸入框）
 * - Enter: 選擇當前焦點項目
 *
 * @example
 * ```ts
 * const { focusedIndex, isFocused, resetFocus } = useKeyboardNavigation({
 *   enabled: computed(() => visible.value),
 *   itemCount: computed(() => options.value.length),
 *   containerRef: dropdownRef,
 *   onSelect: (index) => handleOptionClick(options.value[index])
 * })
 * ```
 */
export const useKeyboardNavigation = (options: UseKeyboardNavigationOptions) => {
  const { enabled, itemCount, containerRef, focusedSelector = '.focused', inputRef, onSelect, resetTrigger } = options

  /** 當前焦點索引（-1 表示焦點在輸入框） */
  const focusedIndex = ref(FOCUS_ON_INPUT)

  /**
   * 滾動到焦點項目
   */
  const scrollToFocusedOption = () => {
    nextTick(() => {
      const focusedOption = containerRef.value?.querySelector(focusedSelector)
      if (focusedOption) {
        focusedOption.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      }
    })
  }

  /**
   * 重置焦點到輸入框
   */
  const resetFocus = () => {
    focusedIndex.value = FOCUS_ON_INPUT
  }

  /**
   * 判斷指定索引是否為當前焦點
   * @param index 要判斷的索引
   */
  const isFocused = (index: number): boolean => {
    return focusedIndex.value === index
  }

  /**
   * 設置焦點到指定索引
   * @param index 目標索引
   */
  const setFocusedIndex = (index: number) => {
    focusedIndex.value = index
  }

  // 處理向下鍵
  onKeyStroke('ArrowDown', (e) => {
    if (!enabled.value || itemCount.value === 0) {
      return
    }

    e.preventDefault()

    if (focusedIndex.value === FOCUS_ON_INPUT) {
      // 從輸入框移到第一個選項
      focusedIndex.value = 0
      scrollToFocusedOption()
    } else if (focusedIndex.value < itemCount.value - 1) {
      // 在列表中向下移動
      focusedIndex.value++
      scrollToFocusedOption()
    }
  })

  // 處理向上鍵
  onKeyStroke('ArrowUp', (e) => {
    if (!enabled.value || itemCount.value === 0) {
      return
    }

    e.preventDefault()

    if (focusedIndex.value > 0) {
      focusedIndex.value--
      scrollToFocusedOption()
    } else if (focusedIndex.value === 0) {
      focusedIndex.value = FOCUS_ON_INPUT
      // 焦點回到輸入框
      inputRef?.value?.focus()
    }
  })

  // 處理 Enter 鍵
  onKeyStroke('Enter', (e) => {
    if (!enabled.value || itemCount.value === 0) {
      return
    }

    if (focusedIndex.value >= 0 && focusedIndex.value < itemCount.value) {
      e.preventDefault()
      onSelect?.(focusedIndex.value)
    }
  })

  // 監聽重置觸發源
  if (resetTrigger) {
    watch(resetTrigger, () => {
      resetFocus()
    })
  }

  return {
    /** 當前焦點索引（-1 表示焦點在輸入框） */
    focusedIndex,

    /** 判斷指定索引是否為當前焦點 */
    isFocused,

    /** 重置焦點到輸入框 */
    resetFocus,

    /** 設置焦點到指定索引 */
    setFocusedIndex,

    /** 滾動到焦點項目 */
    scrollToFocusedOption
  }
}
